import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin, Waves, Clock, Phone, Coffee, Car, Wifi } from "lucide-react";
import { BUSINESS_SCHEMA, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/fort-erie-bed-and-breakfast")({
  head: () => ({
    meta: [
      { title: "Fort Erie Bed and Breakfast - Malak BnB Resort" },
      {
        name: "description",
        content:
          "Malak BnB Resort is a boutique bed and breakfast in Fort Erie's Crystal Beach community, near Lake Erie, the Peace Bridge, and Niagara Falls.",
      },
      { property: "og:title", content: "Fort Erie Bed and Breakfast - Malak BnB Resort" },
      {
        property: "og:description",
        content:
          "A boutique BnB in Fort Erie's Crystal Beach community, five minutes from Bay Beach and 15 minutes from the Peace Bridge.",
      },
      { property: "og:url", content: "https://malakbnbresort.ca/fort-erie-bed-and-breakfast" },
      { property: "og:image", content: "/images/hero-exterior.webp" },
      { "script:ld+json": BUSINESS_SCHEMA },
      { "script:ld+json": faqPageSchema(FAQ) },
    ],
    links: [canonical("/fort-erie-bed-and-breakfast")],
  }),
  component: FortEriePage,
});

const NEARBY = [
  { icon: Waves, label: "Bay Beach, Crystal Beach", detail: "5-minute walk" },
  { icon: MapPin, label: "Downtown Fort Erie", detail: "10-minute drive" },
  { icon: Clock, label: "Peace Bridge to Buffalo, NY", detail: "15-minute drive" },
  { icon: MapPin, label: "Ridgeway", detail: "5-minute drive" },
  { icon: MapPin, label: "Niagara Falls", detail: "30-minute drive" },
  { icon: MapPin, label: "Port Colborne", detail: "25-minute drive" },
];

const INCLUDED = [
  { icon: Coffee, label: "Continental breakfast buffet" },
  { icon: Waves, label: "Outdoor heated pool" },
  { icon: Wifi, label: "Free Wi-Fi" },
  { icon: Car, label: "Free parking" },
];

const FAQ = [
  {
    q: "Is Malak BnB Resort located in Fort Erie?",
    a: "Yes. Malak BnB Resort is at 264 Ridge Road South in Crystal Beach, a lakeside community within the Town of Fort Erie, Ontario. Downtown Fort Erie is about a 10-minute drive from the property.",
  },
  {
    q: "How close is Malak BnB Resort to the Peace Bridge and Buffalo?",
    a: "Malak BnB Resort is roughly a 15-minute drive from the Peace Bridge, the border crossing into Buffalo, New York. Many guests use it as a quieter Canadian base for a cross-border weekend.",
  },
  {
    q: "Does Malak BnB Resort have a pool?",
    a: "Yes. Malak BnB Resort has an outdoor heated swimming pool, along with indoor and outdoor lounge areas, available to guests throughout their stay in Fort Erie.",
  },
  {
    q: "What rooms are available at Malak BnB Resort in Fort Erie?",
    a: "Malak BnB Resort offers four room types: 2 Double Beds (450 sq ft, from C$345), Standard Double Bed (400 sq ft, from C$345), Deluxe Queen Bed (400 sq ft, from C$355), and Queen Bed Suite (440 sq ft, from C$380).",
  },
  {
    q: "How do I request a reservation at Malak BnB Resort?",
    a: "Submit a reservation request through the Malak BnB Resort booking form with your preferred room and dates. The team personally reviews and confirms availability, then sends payment details directly. There is no instant booking.",
  },
];

function FortEriePage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/hero-exterior.webp"
          alt="Exterior of Malak BnB Resort in Fort Erie's Crystal Beach community, Ontario"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">
              Fort Erie, Ontario
            </p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              Fort Erie Bed and Breakfast Near Lake Erie
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">
            Where the Property Sits
          </p>
          <p className="text-2xl font-heading text-foreground leading-snug mb-8">
            Malak BnB Resort sits in Crystal Beach, a lakeside community inside the Town of Fort
            Erie.
          </p>
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              Fort Erie sits at the southern tip of the Niagara Region, right where Lake Erie meets
              the Niagara River. It is quieter than the busier corridor up toward Niagara Falls, and
              its Crystal Beach community is where most visitors actually want to stay: a short walk
              to sand, a slower pace, and still close enough to reach everything else the region has
              to offer.
            </p>
            <p>
              Malak BnB Resort is at 264 Ridge Road South in Crystal Beach. Four rooms, hardwood
              floors, plush bedding, an outdoor heated pool, and continental breakfast every
              morning. It is family-owned and run, and the address puts guests a five-minute walk
              from Bay Beach and about a ten-minute drive from downtown Fort Erie.
            </p>
            <p>
              For visitors crossing from the United States, the Peace Bridge into Buffalo, New York
              is about 15 minutes away, which makes Fort Erie a common first or last stop on a
              Niagara Region trip.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Request to Book
            </Link>
            <Link
              to="/rooms"
              className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
            >
              View Rooms and Rates
            </Link>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
                What Is Included
              </p>
              <h2 className="text-3xl font-heading text-foreground leading-snug mb-6">
                A Comfortable Stay in Fort Erie
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Every room at Malak BnB Resort comes with the basics done properly. Breakfast is
                ready each morning, the pool is heated, the Wi-Fi works, and parking is free. It is
                a family-run place, and that shows up in the small details.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {INCLUDED.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-3 bg-background rounded-xl p-4 border border-border/50"
                  >
                    <item.icon className="h-5 w-5 text-primary shrink-0" />
                    <span className="text-sm font-medium text-foreground">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/gallery-pool-outdoor.webp"
                alt="Outdoor heated pool at Malak BnB Resort in Fort Erie, Ontario"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Nearby */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/beach-bay.webp"
                alt="Bay Beach at Crystal Beach, Fort Erie, Ontario on Lake Erie"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
                Getting Around
              </p>
              <h2 className="text-3xl font-heading text-foreground leading-snug mb-6">
                Close to Everything in the Fort Erie Area
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Crystal Beach keeps you close to the water and the quiet part of town, while
                downtown Fort Erie, Ridgeway, the Peace Bridge, and Niagara Falls are all within a
                short drive.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {NEARBY.map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <item.icon className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/crystal-beach-bed-and-breakfast"
                className="mt-8 inline-flex items-center text-primary font-semibold text-sm hover:underline"
              >
                Read more about staying in Crystal Beach →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              Common Questions
            </p>
            <h2 className="text-3xl font-heading text-foreground">About Staying in Fort Erie</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">
            Fort Erie, Ontario
          </p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Ready to stay near Fort Erie and Lake Erie?
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the Malak team will personally confirm your stay. No
            third-party fees, no automated confirmation, just a direct connection.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg"
            >
              Request Your Stay
            </Link>
            <a
              href="tel:+12899298055"
              className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors"
            >
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
